import { Booking } from "../model";
import React from "react";
import { CheckIcon, InfoIcon, ZapIcon } from "lucide-react";
import YachtSection from "./sections/YachtSection/YachtSection";
import ExternalBookingLinksSection from "./sections/ExternalBookingLinksSection";
import UsersSection from "./sections/UsersSection/UsersSection";
import ExternalBookingDetailsSection from "./sections/ExternalBookingDetailsSection";
import PricingSection from "./sections/PricingSection";
import GoodToKnowSection from "./sections/GoodToKnowSection";
import InternalNotesSection from "./sections/InternalNotesSection";
import YachtBrokerNotesSection from "./sections/YachtBrokerNotesSection";
import { useBookingData } from "@/src/hooks/useBookingData";
import RevertBookingToOffer from "../RevertBookingToOffer/RevertBookingToOffer";
import MailingSection from "./sections/MailingSection";
import { EquipmentAndExtras } from "./EquipmentAndExtras";
import { Confirmation, ConfirmationSummary } from "../ConfirmTab";
import { Button } from "@/src/components/ui/button";
import { charterProfile, disclosureFor } from "../charterProfile";

/**
 * Overview (v14 design, ws-booking.js): hero → disclosure strip → confirmation
 * state → confirmation summary → Charter & extras (editable) → links & people
 * → pricing → good to know & notes. The confirm summary lives here whenever
 * the Confirm tab is hidden, so it's never lost once the deal advances.
 */
const SectionTitle = ({ children, hint }: { children: React.ReactNode; hint?: string }) => (
  <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
    {children}
    {hint && <span className="ml-2 font-normal normal-case tracking-normal text-slate-400">· {hint}</span>}
  </p>
);

const humanStatus = (s: string | null | undefined) => (s ? s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) : "");
const day = (s: string | null | undefined) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null);

function ConfirmationBanner({ booking, c, showConfirmTab, onGo }: { booking: Booking; c: Confirmation | null; showConfirmTab: boolean; onGo: (tab: string) => void }) {
  if (c?.confirmation) {
    const x = c.confirmation;
    const via = x.meta && (x.meta as any).confirmedVia === "helm-app-inbox" ? "Confirmed from the payments inbox" : x.paymentSource ? `Confirmed against ${x.paymentSource}` : "Confirmed";
    return (
      <div className="flex items-center gap-3 rounded-lg border border-green-300 bg-green-50 px-4 py-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-600 text-white"><CheckIcon className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900">Booking confirmed{x.at ? ` · ${day(x.at)}` : ""}</p>
          <p className="text-sm text-slate-600">
            {via}{x.opsUser ? ` by ${x.opsUser}` : ""}{x.xeroInvoiceNumber ? ` · Xero invoice ${x.xeroInvoiceNumber} issued` : ""}
            {booking.contractStatus === "countersigned" ? " · contract countersigned." : "."}
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={() => document.getElementById("confirmation-summary")?.scrollIntoView({ behavior: "smooth", block: "start" })}>View confirmation</Button>
      </div>
    );
  }
  if (showConfirmTab) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-600 text-white"><ZapIcon className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900">Not yet confirmed · {humanStatus(c?.status ?? booking.hubspotStatus)}</p>
          <p className="text-sm text-slate-600">No Xero invoice has been issued. Confirming resolves the client's Xero contact, opens a per-deal charter account and issues the ACCREC invoice.</p>
        </div>
        <Button size="sm" onClick={() => onGo("confirm")}>Confirm booking →</Button>
      </div>
    );
  }
  return null;
}

export const Overview = ({
  booking,
  handleCopyToClipboard,
  handleYachtDetailsModal,
  yachtDetailsOpen,
  yachtId,
  setYachtDetailsOpen,
  confirmation,
  showConfirmTab,
  onGo,
}: {
  booking: Booking;
  handleCopyToClipboard: (text: string) => void;
  handleYachtDetailsModal: (id: string) => void;
  yachtDetailsOpen: boolean;
  yachtId: string | null;
  setYachtDetailsOpen: (value: boolean) => void;
  confirmation: Confirmation | null;
  showConfirmTab: boolean;
  onGo: (tab: string) => void;
}) => {
  const { brokerData, amenitiesData, operatorData } = useBookingData(booking);
  const profile = charterProfile(booking);
  return (
    <React.Fragment>
      <YachtSection
        booking={booking}
        handleYachtDetailsModal={handleYachtDetailsModal}
        yachtDetailsOpen={yachtDetailsOpen}
        yachtId={yachtId}
        setYachtDetailsOpen={setYachtDetailsOpen}
        amenities={amenitiesData ?? []}
        operator={operatorData}
      />
      <p className="flex items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm text-slate-700">
        <InfoIcon className="h-4 w-4 shrink-0 text-blue-600" />
        <span>Showing the <b>{profile.label}</b> layout — {disclosureFor(profile.kind)}</span>
      </p>
      <ConfirmationBanner booking={booking} c={confirmation} showConfirmTab={showConfirmTab} onGo={onGo} />
      {!showConfirmTab && confirmation?.confirmation && (
        <div id="confirmation-summary">
          <ConfirmationSummary c={confirmation} booking={booking} />
        </div>
      )}
      <SectionTitle hint="editable">Charter &amp; extras</SectionTitle>
      <EquipmentAndExtras booking={booking} />
      <SectionTitle>Links &amp; people</SectionTitle>
      <ExternalBookingLinksSection
        dealLink={booking.offer?.dealLink}
        offerId={booking.offer.id}
        hubspotId={booking.hubspotId}
        bookingSlug={booking.slug}
        bookingId={booking.id}
        handleCopyToClipboard={handleCopyToClipboard}
        brand={booking.brand}
      />
      <UsersSection booking={booking} broker={brokerData} />
      <ExternalBookingDetailsSection booking={booking} />
      <SectionTitle>Pricing</SectionTitle>
      <PricingSection booking={booking} />
      <MailingSection booking={booking} />
      <SectionTitle>Good to know &amp; notes</SectionTitle>
      <GoodToKnowSection booking={booking} operator={operatorData} />
      <InternalNotesSection booking={booking} />
      <YachtBrokerNotesSection booking={booking} />
      <RevertBookingToOffer bookingId={booking.id} />
    </React.Fragment>
  );
};
